/**
 * An id for THIS browser tab.
 *
 * Sent on every request as `x-client-id` and echoed back on the realtime
 * feed, so a tab can tell "someone changed this" apart from "I changed this".
 * The distinction matters twice: a tab must not refetch a list it has just
 * refreshed itself, and a "this record was updated by someone else" banner
 * must never fire for the user's own save.
 *
 * `sessionStorage` scopes the id to the tab and survives a reload of it; a
 * duplicated or new tab gets its own, which is what we want — two tabs of one
 * user are two screens that can be out of date with each other.
 */
const STORAGE_KEY = 'dentacares.clientId';

function generate(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

let cached: string | null = null;

export function getClientId(): string {
  if (cached) return cached;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      cached = stored;
      return stored;
    }
    const fresh = generate();
    sessionStorage.setItem(STORAGE_KEY, fresh);
    cached = fresh;
    return fresh;
  } catch {
    // Private mode or blocked storage: the id then lives for the page only.
    cached = generate();
    return cached;
  }
}
